import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  usePayment,
  usePaymentMethods,
  setPaymentError,
  setPaymentIsProcessing,
  setPaymentMethodsLoading,
  setPaymentMethodsError,
  useTicket,
  useConfig,
  setShowAddPaymentForm,
  setSurchargeData,
  setSelectedPaymentMethod,
  setCardData,
  setShowSurchargeConfirmationModal,
  useSurcharge,
} from "../../store/Store";
import {
  fetchPaymentMethods as doFetchPaymentMethods,
  deletePaymentMethod,
} from "../payment/utils/paymentMethodsHelpers";
import { paymentFailedStatuses } from "../general/constants";
import { useTranslation } from "react-i18next";
import { PlusIcon } from "../general/Icons";
import { fetchTicketData, getBalanceDue } from "../general/AppHelpers";
import {
  addSurchargeToTicketAdjustments,
  calculateSurcharge,
  isCloud9,
  isCloud9SurchargePassthroughEnabled,
  isSurchargeApplicable,
  isSurchargeRatesV2Enabled,
  removeSurchargeFromTicketAdjustments,
  resolveCardFundingType,
} from "../surcharge/api";
import SurchargeConfirmationModal from "../surcharge/modal";
import ApiWrapper from "../general/ApiWrapper";
import Currency from "../general/Currency";
import CreditCardForm from "../payment/forms/CreditCardForm";
import SavedCreditCard from "./SavedCreditCard";
import Modal from "../general/Modal";

const SavedPaymentMethods = () => {
  const { t } = useTranslation();

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState(null);

  const { isProcessing: processingPayment } = usePayment();
  const {
    savedPaymentMethods: paymentMethods,
    selectedPaymentMethod,
    loading: paymentMethodsLoading,
    error: paymentMethodsError,
    showAddPaymentForm,
  } = usePaymentMethods();
  const { data: ticketData } = useTicket();
  const { accessToken } = useConfig();
  const { showSurchargeConfirmationModal, data: surchargeData } = useSurcharge();

  // Determine the payment amount from access token or balance due
  const paymentAmount =
    getBalanceDue(ticketData) + (ticketData?.resource?.surcharge_amount || 0);

  const client = useMemo(() => ticketData?.resource?.client, [ticketData]);

  const savedPaymentMethod = useMemo(
    () => paymentMethods?.[0],
    [paymentMethods]
  );

  // Fetch saved payment methods
  const fetchPaymentMethods = useCallback(async () => {
    setPaymentMethodsLoading(true);
    setPaymentMethodsError(null);

    if (client?.walk_in) {
      return;
    }

    doFetchPaymentMethods({ accessToken });
  }, [accessToken, client]);

  useEffect(() => {
    setShowAddPaymentForm(false);
  }, []);

  // Load payment methods on component mount
  useEffect(() => {
    if (accessToken) {
      fetchPaymentMethods();
    }
  }, [accessToken, client, fetchPaymentMethods]);

  const isSurchargeApplicableMemoized = useMemo(() => {
    return isSurchargeApplicable(ticketData);
  }, [ticketData]);

  const isSurchargeRatesV2EnabledMemoized = useMemo(
    () => isSurchargeRatesV2Enabled(ticketData),
    [ticketData]
  );

  // Calculate Surcharge
  const doCalculateSurcharge = useCallback(
    async (card_type, card_country) => {
      if (!card_type) return;

      const { surcharge } = await calculateSurcharge(
        accessToken,
        card_type,
        card_country,
        getBalanceDue(ticketData)
      );
      if (surcharge.successful && surcharge.surcharge_configuration_item) {
        addSurchargeToTicketAdjustments(ticketData, surcharge);
        setSurchargeData(surcharge);
      }
    },
    [accessToken, ticketData]
  );

  useEffect(() => {
    if (paymentMethods.length === 0) {
      removeSurchargeFromTicketAdjustments(ticketData);
      return;
    }
    // Skip recalculation when user is adding a new card — we don't know yet
    // which card they'll use, so the Cloud9PaymentForm handles it per token.
    if (showAddPaymentForm) return;
    if (
      paymentMethods.length !== 0 &&
      (ticketData?.resource?.calculateSurcharge ||
        !isSurchargeApplicableMemoized)
    ) {
      return;
    }
    if (isCloud9(ticketData) && paymentMethods[0]?.funding === "debit") return;

    if (isSurchargeRatesV2EnabledMemoized) {
      doCalculateSurcharge(
        resolveCardFundingType(paymentMethods[0]),
        paymentMethods[0]?.country
      );
    } else {
      doCalculateSurcharge(paymentMethods[0]?.funding);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paymentMethods.length,
    isSurchargeApplicableMemoized,
    ticketData?.resource?.calculateSurcharge,
    showAddPaymentForm,
  ]);

  const proceedToPayment = async () => {
    if (isSurchargeApplicableMemoized) setShowSurchargeConfirmationModal(false);

    const paymentMethod = selectedPaymentMethod;

    setPaymentIsProcessing(true);
    setPaymentError(null);
    const { deposit } = ticketData?.access_token?.data?.payload;
    const surchargeAmount = ticketData.resource?.surcharge_amount || 0;
    const sendSurchargeSeparately =
      surchargeAmount > 0 && isCloud9SurchargePassthroughEnabled(ticketData);
    const transactionPayload = {
      card_id: paymentMethod.id,
      payment_method_type: "card",
      amount: paymentAmount - surchargeAmount,
      ...(sendSurchargeSeparately && { surcharge_amount: surchargeAmount }),
      card_type: isSurchargeRatesV2EnabledMemoized
        ? resolveCardFundingType(paymentMethod)
        : paymentMethod.funding,
      deposit,
    };

    if (isSurchargeRatesV2EnabledMemoized) {
      transactionPayload.card_country = paymentMethod.country;
    }

    const { data, error } = await ApiWrapper.post(
      "/api/mkp/v1/intake/transactions",
      transactionPayload,
      { accessToken }
    );

    if (error) {
      setPaymentError({
        type: error?.type,
        message: error.message || t("payment.saved_cc_payment_fail"),
      });
      setPaymentIsProcessing(false);
      return;
    }

    if (paymentFailedStatuses.includes(data?.transaction?.status)) {
      setPaymentError({
        message:
          data?.transaction?.status === paymentFailedStatuses[0]
            ? t("payment.declined")
            : t("payment.failed"),
      });
      setPaymentIsProcessing(false);
      return;
    }

    fetchTicketData(accessToken);
    setSurchargeData(null);
    setPaymentIsProcessing(false);
  };

  // Process payment with selected payment method
  const processPayment = async (paymentMethod) => {
    const cloud9DebitBlocked =
      isCloud9(ticketData) && paymentMethod?.funding === "debit";
    if (
      isSurchargeApplicableMemoized &&
      !cloud9DebitBlocked &&
      (!isSurchargeRatesV2EnabledMemoized ||
        surchargeData?.surcharge_configuration_item)
    ) {
      setShowSurchargeConfirmationModal(true);
    } else {
      await proceedToPayment(paymentMethod);
    }
  };

  // Handle selecting a payment method
  const selectPaymentMethod = (paymentMethod) => {
    if (!paymentMethod) {
      return;
    }

    // Process the payment directly
    setSelectedPaymentMethod(paymentMethod);
    setCardData(paymentMethod);
    processPayment(paymentMethod);
  };

  const onDeletePaymentMethod = useCallback((id) => {
    setPaymentMethodToDelete(id);
    setShowConfirmDelete(true);
  }, []);

  const handleSurchargeConfirmationModalCancel = async () => {
    setShowSurchargeConfirmationModal(false);
  };

  const confirmationModal = useMemo(() => {
    if (!showConfirmDelete) {
      return null;
    }

    return (
      <Modal
        title={t("payment_methods.delete")}
        message={t("payment_methods.delete_confirm")}
        type="info"
        data-testid="confirmation-modal"
        onConfirm={() => {
          deletePaymentMethod({
            id: paymentMethodToDelete,
            accessToken,
            ticketData,
          });
          setShowConfirmDelete(false);
        }}
        onClose={() => setShowConfirmDelete(false)}
      />
    );
  }, [paymentMethodToDelete, showConfirmDelete, accessToken, t, ticketData]);

  return (
    <div className="mt-6" data-testid="saved-payment-methods">
      {paymentMethodsError && (
        <Modal
          title="Error"
          message={paymentMethodsError}
          type="error"
          onClose={() => setPaymentMethodsError(null)}
        />
      )}

      {paymentMethodsLoading || processingPayment ? (
        <div
          className="flex justify-center items-center py-4"
          data-testid="loading-indicator"
        >
          <svg
            className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span>
            {processingPayment
              ? t("payment.processing")
              : t("payment_methods.loading")}
          </span>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            <div className="space-y-2 mb-5 px-0 mt-7 text-sm">
              {!savedPaymentMethod && (
                <div
                  className="text-center py-4 text-black-light"
                  data-testid="no-payment-methods"
                >
                  {t("payment_methods.no_saved_payment_methods")}
                </div>
              )}
              {savedPaymentMethod && (
                <label className="flex items-center h-6">
                  <input
                    type="radio"
                    name="payment-method"
                    className="h-4 w-4 mx-2 text-primary focus:ring-primary border-primary cursor-pointer"
                    checked={!showAddPaymentForm}
                    onChange={(e) =>
                      e.target.checked && setShowAddPaymentForm(false)
                    }
                    data-testid="use-saved-card-radio"
                  />
                  <div className="flex-1">
                    <SavedCreditCard
                      savedPaymentMethod={savedPaymentMethod}
                      onDeletePaymentMethod={onDeletePaymentMethod}
                      data-testid="saved-credit-card"
                    />
                  </div>
                </label>
              )}

              <label className="flex items-center h-6">
                {savedPaymentMethod && (
                  <input
                    type="radio"
                    name="payment-method"
                    checked={showAddPaymentForm}
                    className="h-4 w-4 mx-2 text-primary focus:ring-primary border-primary cursor-pointer"
                    onChange={(e) =>
                      e.target.checked && setShowAddPaymentForm(true)
                    }
                    data-testid="add-new-card-radio"
                  />
                )}
                <div
                  className="flex items-center text-primary cursor-pointer"
                  onClick={() => setShowAddPaymentForm(true)}
                  data-testid="add-payment-method-button"
                >
                  <PlusIcon />
                  <span>{t("payment_methods.add")}</span>
                </div>
              </label>
            </div>

            {showAddPaymentForm ? (
              <div data-testid="credit-card-form-container">
                <CreditCardForm
                  addPaymentMethod={true}
                  data-testid="credit-card-form"
                />
              </div>
            ) : (
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => selectPaymentMethod(paymentMethods[0])}
                  disabled={!savedPaymentMethod}
                  className={`flex-1 mx-2 mb-8 text-base font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline ${
                    !paymentMethods?.length
                      ? "bg-primary-disabled cursor-not-allowed"
                      : "bg-primary hover:bg-primary-hover"
                  } text-white`}
                  data-testid="pay-button"
                >
                  {t("payment.pay")} <Currency value={paymentAmount} />
                </button>
              </div>
            )}
          </div>
        </>
      )}
      {showConfirmDelete && confirmationModal}
      {showSurchargeConfirmationModal && (
        <SurchargeConfirmationModal
          onConfirm={proceedToPayment}
          onClose={handleSurchargeConfirmationModalCancel}
        />
      )}
    </div>
  );
};

export default SavedPaymentMethods;
